// Library Methods
import React, { createContext, useContext, useMemo } from "react";

// Constants
import { DEFAULT_CURRENCY } from "src/constants/enums";

// Hooks
import useLocalStorage from "src/hooks/storage/useLocalStorage";
import { useClinicById } from "src/services/clinics/hooks/useClinicById";
import { useClinicsData } from "src/services/clinics/hooks/useClinicsData";

// Types
import { ClinicType } from "src/services/clinics/types";

type ClinicInfoType = {
  id: string;
  name: string;
  timezone: string | null;
  licensing: ClinicType["licensing"];
  settings: ClinicType["settings"];
};

const defaultClinicInfo: ClinicInfoType = {
  id: "",
  name: "",
  timezone: null,
  licensing: {
    id: "",
    canVideoStream: false,
    canUseDicomReports: false,
    canUsePngReports: false,
    canUseAdvancedEyeTracking: false,
    canDisplayTotalAndPatternDeviationNumericalCharts: false,
    canUseBaileyLovieExams: false,
    canUseExamPackaging: false,
    canUseOcularMotilityExams: false,
    canUseSynoptophoreExams: false,
    canUseAniseikoniaExams: false,
    canUseRAPDExams: false,
    canUseProgressionReports: false,
    canUseExternalReports: false,
    canUseIOLSimulations: false,
    canUseProgressiveSimulations: false,
    canUseEMR: false,
    canPlayVideos: false,
    lastModifiedDate: "",
    lastModifiedBy: "",
  },
  settings: {
    id: "",
    enableVRExamCompleteNotificationSounds: false,
    enablePortalSounds: false,
    currencySymbol: DEFAULT_CURRENCY.split(" - ")[0],
    currencyCode: DEFAULT_CURRENCY.split(" - ")[1],
    clinicDefaultLanguage: "",
    patientDefaultLanguage: "",
    colorVisionDefaultTimeLimit: 0,
    defaultReportType: "PDF",
    monocularReportPdfLayoutOption: "Single",
    reportDisplayLeftEyeFirst: false,
    MTOFixationMethod: false,
    lastModifiedDate: "",
    lastModifiedBy: "",
  },
};

type ClinicContextType = {
  clinicInfo: ClinicInfoType;
  allClinicInfo: Array<ClinicType>;
  setClinicId: (id: string) => void;
  isLoading: boolean;
};

const ClinicContext = createContext<ClinicContextType>({
  clinicInfo: defaultClinicInfo,
  allClinicInfo: [],
  setClinicId: () => {},
  isLoading: false,
});

export const useClinicContext = () => {
  const context = useContext(ClinicContext);
  if (!context) {
    throw new Error(
      "useClinicContext must be used within ClinicContextProvider"
    );
  }

  return context;
};

const ClinicProvider = ({ children }: { children: React.ReactNode }) => {
  const [clinicId, setClinicId] = useLocalStorage("clinicId", "");

  const { data: allClinicsData, isLoading: isLoadingClinics } =
    useClinicsData();

  const { data: clinicData, isLoading: isLoadingClinic } = useClinicById({
    useToken: true,
    clinicId: clinicId || undefined,
  });

  const clinicInfo = useMemo<ClinicInfoType>(
    () =>
      clinicData
        ? {
            id: clinicData.id,
            name: clinicData.name,
            timezone: clinicData.timezone,
            licensing: clinicData.licensing,
            settings: clinicData.settings,
          }
        : defaultClinicInfo,
    [clinicData]
  );

  const allClinicInfo = useMemo(
    () =>
      [...(allClinicsData ?? [])].sort((a, b) =>
        a?.name?.localeCompare(b?.name)
      ),
    [allClinicsData]
  );

  const contextValue = useMemo(
    () => ({
      clinicInfo,
      allClinicInfo,
      setClinicId,
      isLoading: isLoadingClinic || isLoadingClinics,
    }),
    [clinicInfo, allClinicInfo, setClinicId, isLoadingClinic, isLoadingClinics]
  );

  return (
    <ClinicContext.Provider value={contextValue}>
      {children}
    </ClinicContext.Provider>
  );
};

export default ClinicProvider;
